import React, { useEffect, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";
import {
    Card as RebaseCard,
    Chip as RebaseChip,
    Typography as RebaseTypography
} from "@rebasepro/ui";

import { sinceLabel } from "./_prospects_shared";

const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * The invitations a company has open, on its cover, under the people who
 * already have access (drizzle/0070).
 *
 * Read-only here, like the rest of the cover. The company withdraws and
 * resends from its own "Mi empresa"; an admin who has to act can do it from the
 * `company_invitations` collection. What this answers is the question a
 * recruiter has when a client says "le he invitado y no le llega": is there an
 * invitation, did the mail leave, has it lapsed.
 *
 * Each line says only what the row does. `sent_at` empty means our server
 * never handed the mail over, so it says that and not "pendiente".
 */

interface InvitationRow {
    id: string;
    email?: string | null;
    invited_by?: string | null;
    created_at?: string | null;
    sent_at?: string | null;
    expires_at?: string | null;
    accepted_at?: string | null;
    revoked_at?: string | null;
}

interface MemberRow {
    auth_user_id?: string | null;
    contact_name?: string | null;
    contact_email?: string | null;
}

const DAY = new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long" });

function dayLabel(value: string | null | undefined): string {
    if (!value) return "sin fecha";
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? DAY.format(date) : "sin fecha";
}

function where(row: InvitationRow, now: number): { chip: string; color: string; line: string } {
    const expires = row.expires_at ? Date.parse(row.expires_at) : NaN;
    if (!Number.isFinite(expires) || expires <= now) {
        return { chip: "Caducada", color: "grayLighter", line: `Caducó el ${dayLabel(row.expires_at)}; nadie la aceptó.` };
    }
    if (!row.sent_at) {
        return { chip: "Correo no enviado", color: "redLighter", line: "La invitación existe, pero el servidor de correo no llegó a aceptar el mensaje: a esa persona no le ha llegado nada." };
    }
    return { chip: "Pendiente", color: "orangeLighter", line: `Enviada ${sinceLabel(row.sent_at)}; caduca el ${dayLabel(row.expires_at)}.` };
}

export function CompanyInvitationsPanel({ companyId }: { companyId: string }) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;
    const [rows, setRows] = useState<InvitationRow[] | null>(null);
    const [members, setMembers] = useState<MemberRow[]>([]);
    const [failed, setFailed] = useState(false);
    const wanted = useRef<string | null>(null);

    useEffect(() => {
        if (!data || !companyId) return;
        wanted.current = companyId;
        setRows(null);
        setFailed(false);
        Promise.all([
            data.collection("company-invitations").find({
                where: { company_id: ["==", companyId] },
                orderBy: ["created_at", "desc"],
                limit: 50
            }),
            data.collection("company-members").find({
                where: { company_id: ["==", companyId] },
                limit: 50
            }).catch(() => undefined)
        ]).then(([invitations, memberPage]) => {
            if (wanted.current !== companyId) return;
            setRows((invitations?.data ?? []) as InvitationRow[]);
            setMembers((memberPage?.data ?? []) as MemberRow[]);
        }).catch(() => {
            if (wanted.current !== companyId) return;
            setFailed(true);
        });
    }, [data, companyId]);

    if (failed) {
        return (
            <Typography variant="body2" color="secondary">
                No se pudieron leer sus invitaciones.
            </Typography>
        );
    }

    const open = (rows ?? []).filter(r => !r.accepted_at && !r.revoked_at);
    // Nothing to say is said by saying nothing: a company with no open
    // invitation is the normal case, and "Invitaciones (0)" on every cover
    // would be a heading for an empty room.
    if (open.length === 0) return null;

    const now = Date.now();
    const inviter = (uid: string | null | undefined) => {
        const m = members.find(x => String(x.auth_user_id ?? "") === String(uid ?? ""));
        return m ? ((m.contact_name ?? "").trim() || (m.contact_email ?? "").trim() || null) : null;
    };

    return (
        <div className="flex flex-col gap-2">
            <Typography variant="subtitle2" component="h3">Invitaciones abiertas ({open.length})</Typography>
            {open.map(row => {
                const state = where(row, now);
                const by = inviter(row.invited_by);
                return (
                    <Card key={row.id} className="p-3 flex items-start gap-3 flex-wrap">
                        <div className="grow min-w-0">
                            <Typography variant="body2" className="font-medium break-all">{row.email}</Typography>
                            <Typography variant="body2" color="secondary">
                                {state.line}{by ? ` La envió ${by}.` : ""}
                            </Typography>
                        </div>
                        <Chip size="small" colorScheme={state.color}>{state.chip}</Chip>
                    </Card>
                );
            })}
        </div>
    );
}

export default CompanyInvitationsPanel;
